import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {Observable, Subject} from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { NzNotificationService } from 'ng-zorro-antd/notification';

import { EventService, AuthenticationService, ValidationService } from '../../../_services';
import { SharedService } from '../../../shared/_services/shared.service';
import { environment } from '../../../../environments/environment';
import {ApiService} from '../../../shared/_services/api.service';

@Component({
  templateUrl: './reset.component.html',
  styleUrls: ['../../auth.component.less']},
)

export class PasswordResetComponent implements OnInit, OnDestroy {
  public scope: string;
  token: string;
  signature: string;
  expiresAt: number;
  email: string;
  form: UntypedFormGroup;
  loading = false;
  passwordVisible = false;
  oldPasswordVisible = false;
  passwordConfirmationVisible = false;
  errorMessage: string;
  isCaptchaVerified = false;
  env = environment;
  private reset$: Observable<string>;
  private unsubscribe$ = new Subject<void>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private auth: AuthenticationService,
    private api: ApiService,
    private notification: NzNotificationService,
    private eventService: EventService,
    private validationService: ValidationService,
    private sharedService: SharedService,
  ) {
    this.scope = route.snapshot?.data.scope;
    // redirect to home if already logged in
    if (this.auth.currentUserValue) {
      this.router.navigate(['/']);
    }
  }

  ngOnInit() {
    this.route.params.pipe(takeUntil(this.unsubscribe$)).subscribe(params => {
      this.token = params['token'];
    });

    this.route.queryParams.pipe(takeUntil(this.unsubscribe$)).subscribe(params => {
      this.signature = params['signature'];
      this.expiresAt = params['expires'];
      this.email = params['email'];
    });


    this.form = this.formBuilder.group({
      oldPassword: [null, this.scope === 'expired' ? [Validators.required] : []],
      email: ['', this.scope === 'expired' ? [] : [Validators.required, Validators.email]],
      password: [null, [Validators.required, this.sharedService.passwordValidator]],
      passwordConfirmation: [null, [Validators.required]],
      captcha: [null, this.env.captcha ? [Validators.required] : []],
    }, {
      validators: [this.sharedService.passwordConfirmationValidator, this.oldPasswordValidator]
    });

    // display api validations on form
    this.eventService.eventEmitted.subscribe(event => {
      if (event.name === 'validation-failed') {
        this.validationService.connectErrorsToForm(this.form, event.data);
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  get f() {
    return this.form.controls;
  }

  captchaResolved(isResolved: boolean): void {
    this.isCaptchaVerified = isResolved;
  }

  onSubmit() {
    this.loading = true;
    if (this.scope === 'expired') {
      this.reset$ = this.api.passwordExpiredReset(this.email, this.f.oldPassword.value, this.f.password.value,
        this.f.passwordConfirmation.value);
    } else {
      this.reset$ = this.api.passwordReset(this.f.email.value, this.f.password.value,
        this.f.passwordConfirmation.value, this.token, this.signature, this.expiresAt);
    }
    this.reset$.subscribe(
      () => {
        this.notification.success('Success', 'Password reset successfully.');
        this.router.navigate(['/auth/login']);
      },
      (error) => {
        this.loading = false;
        this.errorMessage = 'Password reset failed.';
        if ( error.status === 503 || error.status === 400){
          this.errorMessage = error.error.message;
        }
        this.notification.error('Error', this.errorMessage);
      }
    );
  }

  oldPasswordValidator = (control: AbstractControl): { [key: string]: any } | null => {
    let error = null;
    if (control) {
      const pass = control.get('password');
      const old = control.get('oldPassword');
      if (pass.value === old.value && pass.dirty && old.dirty) {
        error = { 'samePassword': true };
        control.setErrors(error);
      }
    }
    return error;
  };

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
